/**
 * SERVICE_TERMINAL — the phục vụ face.
 *
 * A waiter's screen answers four questions and nothing else:
 *   1. which tables are mine,
 *   2. who is calling me,
 *   3. which guest orders need me to approve them,
 *   4. what is ready to carry out.
 *
 * "My tables" uses `tables.assignedUserId`. Tables calling for staff and tables
 * awaiting verification are derived from the floor-map status the server
 * already computes (HELP_REQUESTED / WAITING_CONFIRMATION) — the floor-map
 * payload carries no service-request list, so those statuses ARE the signal.
 */

import { useMemo, useState } from 'react';
import { Badge, Button, Card, EmptyState, ErrorState, LoadingState, Tabs, useConfirm, useToast } from '@sonauto/ui';
import { useApi, useAuth } from '../../auth/AuthProvider';
import { usePoll, useAsync, errorMessage } from '../../lib/hooks';
import { useOutbox } from '../../offline/OutboxProvider';
import { Chrome } from '../../components/Chrome';
import { hhmm, vnd } from '../../lib/format';
import type { FloorMapResponse, FloorMapTable, SessionDetailResponse } from '../../lib/types';

type Tab = 'MINE' | 'CALLING' | 'VERIFY' | 'READY' | 'CLEANING';

export function ServiceShell({ onActivateDevice }: { readonly onActivateDevice: () => void }): React.JSX.Element {
  const api = useApi();
  const { me } = useAuth();
  const branchId = me?.session.activeBranchId ?? null;
  const userId = me?.user.id ?? null;

  const [tab, setTab] = useState<Tab>('MINE');
  const [openSessionId, setOpenSessionId] = useState<string | null>(null);

  const floor = usePoll<FloorMapResponse>(
    (signal) => api.org.tables(branchId ?? '', undefined, { signal }),
    6_000,
    [branchId],
    { enabled: branchId !== null },
  );

  const tables = useMemo(() => floor.data?.tables ?? [], [floor.data]);

  const buckets = useMemo(
    () => ({
      MINE: tables.filter((t) => t.assignedUserId !== null && t.assignedUserId === userId),
      CALLING: tables.filter((t) => t.status === 'HELP_REQUESTED' || t.status === 'PAYMENT_REQUESTED'),
      VERIFY: tables.filter((t) => t.status === 'WAITING_CONFIRMATION'),
      READY: tables.filter((t) => t.session !== null && t.session.unservedItemCount > 0),
      CLEANING: tables.filter((t) => t.status === 'NEEDS_CLEANING' || t.status === 'SETTING_UP'),
    }),
    [tables, userId],
  );

  if (branchId === null) {
    return (
      <Chrome title="Phục vụ" onActivateDevice={onActivateDevice}>
        <ErrorState title="Chưa chọn chi nhánh" message="Chọn chi nhánh trong menu tài khoản." />
      </Chrome>
    );
  }

  return (
    <Chrome
      title="Phục vụ"
      onActivateDevice={onActivateDevice}
      actions={
        <Tabs
          variant="pill"
          ariaLabel="Nhóm bàn"
          value={tab}
          onChange={(id) => setTab(id as Tab)}
          items={[
            { id: 'MINE', label: 'Bàn của tôi', badge: buckets.MINE.length },
            { id: 'CALLING', label: 'Khách gọi', badge: buckets.CALLING.length },
            { id: 'VERIFY', label: 'Chờ duyệt', badge: buckets.VERIFY.length },
            { id: 'READY', label: 'Chờ ra món', badge: buckets.READY.length },
            { id: 'CLEANING', label: 'Dọn / setup', badge: buckets.CLEANING.length },
          ]}
        />
      }
    >
      <div className="h-full overflow-y-auto p-3">
        {floor.initialLoading ? <LoadingState label="Đang tải bàn…" /> : null}
        {floor.error !== null && floor.data === null ? (
          <ErrorState message={floor.error.message} onRetry={floor.reload} />
        ) : null}

        {openSessionId !== null ? (
          <VerifyPanel
            sessionId={openSessionId}
            onClose={() => setOpenSessionId(null)}
            onChanged={() => {
              floor.reload();
            }}
          />
        ) : null}

        {tab === 'CLEANING' ? <CleaningPanel tables={buckets.CLEANING} onChanged={floor.reload} /> : null}

        {tab !== 'CLEANING' ? (
          buckets[tab].length === 0 ? (
            <EmptyState title="Không có bàn nào trong nhóm này" description="Danh sách tự cập nhật mỗi 6 giây." />
          ) : (
            <ul className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
              {buckets[tab].map((table) => (
                <li key={table.id}>
                  <TableCard table={table} onOpen={() => setOpenSessionId(table.session?.id ?? null)} />
                </li>
              ))}
            </ul>
          )
        ) : null}
      </div>
    </Chrome>
  );
}

function TableCard({
  table,
  onOpen,
}: {
  readonly table: FloorMapTable;
  readonly onOpen: () => void;
}): React.JSX.Element {
  return (
    <Card
      title={table.displayName}
      description={`${table.seats} chỗ · ${table.status}`}
      actions={
        table.status === 'HELP_REQUESTED' ? (
          <Badge tone="warning">Khách gọi</Badge>
        ) : table.status === 'PAYMENT_REQUESTED' ? (
          <Badge tone="danger">Xin thanh toán</Badge>
        ) : null
      }
    >
      {table.session === null ? (
        <p className="text-sm text-fg-subtle">Bàn trống</p>
      ) : (
        <div className="space-y-1 text-sm">
          <p className="text-fg-muted">
            Mở lúc {hhmm(table.session.openedAt)} · {table.session.guestCount} khách
          </p>
          <p className="text-fg">
            {vnd(table.session.totalMinor)}
            {table.session.unservedItemCount > 0 ? ` · ${table.session.unservedItemCount} món chờ ra` : ''}
          </p>
          <Button className="mt-2" size="lg" fullWidth onClick={onOpen}>
            Mở phiếu bàn
          </Button>
        </div>
      )}
    </Card>
  );
}

/**
 * Verify a guest-submitted order, line by line.
 *
 * WHAT THE API SUPPORTS: accepting the whole order (`POST /orders/:id/confirm`)
 * and rejecting individual lines (`DELETE /orders/:id/items/:itemId` with a
 * reason). "Adjust" is a quantity PATCH.
 *
 * WHAT IT DOES NOT: there is no per-line accept and no partial-confirm — confirm
 * fires EVERY unsent line at once. So the flow here is: reject/adjust the lines
 * you disagree with first, then confirm the rest. That is stated on screen so a
 * waiter is not surprised.
 */
function VerifyPanel({
  sessionId,
  onClose,
  onChanged,
}: {
  readonly sessionId: string;
  readonly onClose: () => void;
  readonly onChanged: () => void;
}): React.JSX.Element {
  const api = useApi();
  const toast = useToast();
  const confirm = useConfirm();
  const outbox = useOutbox();
  const detail = useAsync<SessionDetailResponse>((signal) => api.orders.session(sessionId, { signal }), [sessionId]);

  const reload = (): void => {
    detail.reload();
    onChanged();
  };

  const accept = async (orderId: string): Promise<void> => {
    try {
      const result = await api.orders.confirm(orderId);
      toast.success(`Đã duyệt và gửi bếp ${result.sentItemCount} món`);
      reload();
    } catch (cause: unknown) {
      if (!outbox.snapshot.online) {
        await outbox.queue({
          kind: 'ORDER_CONFIRM',
          label: `Duyệt đơn ${orderId.slice(-6)}`,
          request: { method: 'POST', path: `/v1/orders/${orderId}/confirm`, body: {} },
        });
        toast.warning('Đang ngoại tuyến', 'Lệnh duyệt đã lưu vào hàng đợi.');
        return;
      }
      toast.error('Không duyệt được đơn', errorMessage(cause));
    }
  };

  const reject = async (orderId: string, itemId: string, name: string): Promise<void> => {
    const reason = await confirm({
      title: `Từ chối món "${name}"`,
      requireReason: true,
      reasonLabel: 'Lý do từ chối',
      reasonSuggestions: ['Hết món', 'Khách gọi nhầm', 'Không phục vụ giờ này'],
      tone: 'danger',
    });
    if (reason === null) return;
    try {
      await api.orders.cancelItem(orderId, itemId, reason);
      toast.success('Đã từ chối món');
      reload();
    } catch (cause: unknown) {
      toast.error('Không từ chối được', errorMessage(cause));
    }
  };

  const adjust = async (orderId: string, itemId: string, quantity: number, current: SessionDetailResponse): Promise<void> => {
    const item = current.orders.flatMap((o) => o.items).find((i) => i.id === itemId);
    if (item === undefined) return;
    try {
      // The API's PATCH behaves like a PUT for note/spice/course/priority, so
      // the current values MUST be resent or they are wiped.
      await api.orders.patchItem(orderId, itemId, {
        quantity,
        noteVi: null,
        spiceLevel: null,
        courseNo: 1,
        priority: 0,
      });
      toast.success('Đã chỉnh số lượng');
      reload();
    } catch (cause: unknown) {
      toast.error('Không chỉnh được', errorMessage(cause));
    }
  };

  return (
    <Card
      className="mb-4"
      title="Duyệt đơn khách gửi"
      actions={
        <Button variant="ghost" onClick={onClose}>
          Đóng
        </Button>
      }
    >
      {detail.initialLoading ? <LoadingState compact /> : null}
      {detail.error !== null ? <ErrorState message={detail.error.message} onRetry={detail.reload} /> : null}
      {detail.data?.orders
        .filter((o) => o.status === 'PENDING_CONFIRMATION' || o.status === 'CONFIRMED')
        .map((order) => (
          <div key={order.id} className="mb-4">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="text-sm font-semibold text-fg">Đơn {order.order_no}</h3>
              <Badge tone="info" size="sm">
                {order.channel}
              </Badge>
              <Button size="sm" className="ml-auto" onClick={() => void accept(order.id)}>
                Duyệt tất cả & gửi bếp
              </Button>
            </div>
            <ul className="mt-2 divide-y divide-line">
              {order.items.map((item) => (
                <li key={item.id} className="flex flex-wrap items-center gap-2 py-2">
                  <span className="w-8 text-center font-bold sa-tabular">{item.quantity}</span>
                  <span className="text-flow min-w-0 flex-1 text-sm">{item.product_name_snapshot}</span>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() =>
                      void adjust(order.id, item.id, Math.max(1, item.quantity - 1), detail.data as SessionDetailResponse)
                    }
                  >
                    −1
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() =>
                      void adjust(order.id, item.id, item.quantity + 1, detail.data as SessionDetailResponse)
                    }
                  >
                    +1
                  </Button>
                  <Button
                    size="sm"
                    variant="danger"
                    onClick={() => void reject(order.id, item.id, item.product_name_snapshot)}
                  >
                    Từ chối
                  </Button>
                </li>
              ))}
            </ul>
          </div>
        ))}
      <p className="text-flow text-xs text-fg-subtle">
        Máy chủ chỉ có “duyệt cả đơn” (<code className="font-mono">POST /orders/:id/confirm</code> gửi mọi món chưa
        gửi). Hãy từ chối hoặc chỉnh các món không đồng ý trước, rồi mới bấm duyệt.
      </p>
    </Card>
  );
}

/**
 * Dọn bàn / setup bàn — wired to `POST /v1/tables/:id/clean` and `/setup`
 * (routes/tables-ops.ts).
 *
 * Both are OFFLINE-QUEUEABLE by design: the permissions are `offlineAllowed`,
 * the endpoint takes an optional Idempotency-Key, and a queued tap that lands
 * after the world moved on answers `{ alreadyDone: true }` instead of 409 —
 * exactly what a runner in a dead-zone hallway needs. The server also closes
 * any open CLEAN_TABLE / SETUP_TABLE task in the same batch, so the physical
 * work counts for the person's KPI.
 */
function CleaningPanel({
  tables,
  onChanged,
}: {
  readonly tables: readonly FloorMapTable[];
  readonly onChanged: () => void;
}): React.JSX.Element {
  const api = useApi();
  const toast = useToast();
  const outbox = useOutbox();
  const [busyTableId, setBusyTableId] = useState<string | null>(null);

  const complete = async (table: FloorMapTable, kind: 'clean' | 'setup'): Promise<void> => {
    const label = kind === 'clean' ? 'Dọn xong' : 'Setup xong';
    setBusyTableId(table.id);
    try {
      const result = await (kind === 'clean' ? api.tables.clean(table.id) : api.tables.setup(table.id));
      toast.success(
        result.alreadyDone === true ? `${table.displayName}: đã được xác nhận trước đó` : `${table.displayName}: ${label.toLowerCase()}`,
      );
      onChanged();
    } catch (cause: unknown) {
      if (!outbox.snapshot.online) {
        await outbox.queue({
          kind: kind === 'clean' ? 'TABLE_CLEAN' : 'TABLE_SETUP',
          label: `${label} — ${table.displayName}`,
          request: { method: 'POST', path: `/v1/tables/${table.id}/${kind}`, body: {} },
        });
        toast.warning('Đang ngoại tuyến', 'Xác nhận đã lưu vào hàng đợi và sẽ gửi khi có mạng.');
        return;
      }
      toast.error(`Không xác nhận được ${label.toLowerCase()}`, errorMessage(cause));
    } finally {
      setBusyTableId(null);
    }
  };

  return (
    <div className="space-y-3">
      {tables.length === 0 ? (
        <EmptyState title="Không có bàn nào chờ dọn / setup" description="Danh sách tự cập nhật mỗi 6 giây." />
      ) : null}
      <ul className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {tables.map((table) => (
          <li key={table.id}>
            <Card
              title={table.displayName}
              description={table.status === 'NEEDS_CLEANING' ? 'Chờ dọn bàn' : 'Chờ setup bàn'}
            >
              {table.status === 'NEEDS_CLEANING' ? (
                <Button
                  size="touch"
                  fullWidth
                  loading={busyTableId === table.id}
                  onClick={() => void complete(table, 'clean')}
                >
                  Đã dọn xong
                </Button>
              ) : (
                <Button
                  size="touch"
                  fullWidth
                  variant="success"
                  loading={busyTableId === table.id}
                  onClick={() => void complete(table, 'setup')}
                >
                  Setup xong — bàn sẵn sàng
                </Button>
              )}
              <p className="text-flow mt-2 text-xs text-fg-subtle">
                {table.status === 'NEEDS_CLEANING'
                  ? 'Dọn xong bàn sẽ chuyển sang “đang setup”.'
                  : 'Setup xong bàn trở về trạng thái trống, nhận khách được ngay.'}
              </p>
            </Card>
          </li>
        ))}
      </ul>
    </div>
  );
}
